import { useState, useRef, useCallback, useEffect } from 'react'

interface SpeechRecognitionResult {
  transcript: string
  confidence: number
}

interface UseSpeechRecognitionOptions {
  /**
   * Called whenever the mic detects the user engaging (audio/sound/speech
   * detected, or a result arrives). Lets a session's idle timer treat reading
   * aloud as activity even though it fires no pointer/keyboard events.
   */
  onActivity?: () => void
}

interface UseSpeechRecognitionReturn {
  isListening: boolean
  isSupported: boolean
  result: SpeechRecognitionResult | null
  error: string | null
  startListening: () => void
  stopListening: () => void
}

export function useSpeechRecognition(options: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionReturn {
  const [isListening, setIsListening] = useState(false)
  const [result, setResult] = useState<SpeechRecognitionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<any>(null)

  const onActivityRef = useRef(options.onActivity)
  useEffect(() => {
    onActivityRef.current = options.onActivity
  }, [options.onActivity])
  const signalActivity = useCallback(() => {
    onActivityRef.current?.()
  }, [])

  const SpeechRecognition =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition

  const isSupported = !!SpeechRecognition

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser')
      return
    }

    setResult(null)
    setError(null)

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = false
    recognition.maxAlternatives = 1
    recognition.lang = 'en-US'

    // Reading aloud fires no pointer/keyboard events, so surface the mic's own
    // engagement signals to keep the session's idle timer from tripping.
    recognition.onaudiostart = () => signalActivity()
    recognition.onsoundstart = () => signalActivity()
    recognition.onspeechstart = () => signalActivity()

    recognition.onresult = (event: any) => {
      signalActivity()
      const speechResult = event.results[0][0]
      setResult({
        transcript: speechResult.transcript.toLowerCase().trim(),
        confidence: speechResult.confidence,
      })
      setIsListening(false)
    }

    recognition.onerror = (event: any) => {
      if (event.error === 'no-speech') {
        setError("Didn't hear anything — try again!")
      } else if (event.error === 'audio-capture') {
        setError('No microphone found')
      } else if (event.error === 'not-allowed') {
        setError('Microphone access denied')
      } else {
        setError('Something went wrong — try again')
      }
      setIsListening(false)
    }

    recognition.onend = () => {
      setIsListening(false)
    }

    recognitionRef.current = recognition
    recognition.start()
    setIsListening(true)
    signalActivity()
  }, [SpeechRecognition, signalActivity])

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }
    setIsListening(false)
  }, [])

  return { isListening, isSupported, result, error, startListening, stopListening }
}
